import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Wand2 } from "lucide-react";

const PURPOSES = [
  { value: "iep_meeting", label: "Request an IEP / IPP meeting" },
  { value: "accommodation", label: "Request specific accommodations" },
  { value: "follow_up", label: "Follow up on a previous request" },
  { value: "share_concern", label: "Share a concern about my child" },
  { value: "introduce", label: "Introduce my child's needs to a new teacher" },
  { value: "other", label: "Something else" },
];

const PURPOSE_LABEL = Object.fromEntries(PURPOSES.map((p) => [p.value, p.label]));

const TEMPLATES = [
  { id: "iep", label: "Request an IEP meeting", icon: "📅", purpose: "iep_meeting",
    starter: "I'd like to schedule a meeting to review and update my child's IEP. Please let me know a few times that work in the next couple of weeks." },
  { id: "ea", label: "Ask about EA support", icon: "🤝", purpose: "accommodation",
    starter: "I'd like to understand what educational assistant (EA) support is available and whether my child could be considered for additional in-class support." },
  { id: "followup", label: "Follow up after a meeting", icon: "🔁", purpose: "follow_up",
    starter: "Thank you for meeting with me. I'm following up to confirm the next steps we agreed on and to ask about the timeline for putting them in place." },
  { id: "accom", label: "Request accommodations", icon: "🧩", purpose: "accommodation",
    starter: "I'd like to request some classroom accommodations for my child, such as extra time on tests and a quiet space for exams." },
];

export function EmailDraftDialog({ open, onClose, onSubmit }) {
  const [purpose, setPurpose] = useState("iep_meeting");
  const [childName, setChildName] = useState("");
  const [recipient, setRecipient] = useState("");
  const [details, setDetails] = useState("");
  const [activeTpl, setActiveTpl] = useState(null);

  const applyTemplate = (t) => { setPurpose(t.purpose); setDetails(t.starter); setActiveTpl(t.id); };

  const build = () => {
    const lines = [
      "Please draft a warm, clear and professional email I can send to my child's school.",
      `Purpose of the email: ${PURPOSE_LABEL[purpose] || purpose}.`,
    ];
    if (childName.trim()) lines.push(`My child's first name: ${childName.trim()}.`);
    if (recipient.trim()) lines.push(`Who it's addressed to: ${recipient.trim()}.`);
    if (details.trim()) lines.push(`Key points to include:\n${details.trim()}`);
    lines.push(
      "Keep it concise, respectful and easy to read. Start with a clear 'Subject:' line, use a polite greeting and closing, and format any requests as short bullet points where helpful. Do not provide medical, legal or diagnostic claims — keep it as a parent's own words."
    );
    return lines.join("\n");
  };

  const submit = () => { onSubmit(build()); reset(); };
  const reset = () => { setPurpose("iep_meeting"); setChildName(""); setRecipient(""); setDetails(""); setActiveTpl(null); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="email-draft-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail size={18} className="text-kid-teal" /> Draft a school email</DialogTitle>
          <DialogDescription>Tell KidPath a little about what you need — it'll write a calm, clear draft you can copy into your email.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5" data-testid="email-templates">
            <Label>Quick start</Label>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((t) => (
                <button key={t.id} type="button" onClick={() => applyTemplate(t)} data-testid={`email-template-${t.id}`}
                  className={`inline-flex items-center gap-1.5 text-xs font-poppins font-semibold px-3 py-1.5 rounded-full border transition-colors ${activeTpl === t.id ? "bg-kid-teal text-white border-kid-teal" : "bg-white text-kid-ink/80 border-kid-line hover:border-kid-teal hover:text-kid-teal"}`}>
                  <span aria-hidden>{t.icon}</span> {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>What's the email about?</Label>
            <select value={purpose} onChange={(e) => { setPurpose(e.target.value); setActiveTpl(null); }} data-testid="email-purpose" className="h-11 w-full rounded-xl border border-kid-line px-3 bg-white text-sm">
              {PURPOSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Child's first name <span className="text-kid-muted font-normal">(optional)</span></Label>
              <Input value={childName} onChange={(e) => setChildName(e.target.value)} placeholder="e.g. Sam" data-testid="email-child" className="h-11 rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Addressed to <span className="text-kid-muted font-normal">(optional)</span></Label>
              <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="e.g. Ms. Lee, teacher" data-testid="email-recipient" className="h-11 rounded-xl" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>What do you want to say?</Label>
            <Textarea value={details} onChange={(e) => { setDetails(e.target.value); setActiveTpl(null); }} rows={4} data-testid="email-details"
              placeholder="e.g. Sam needs extra time on tests and a quiet space for exams. I'd like to set up a meeting to update the IEP."
              className="rounded-xl resize-none" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="kid-btn kid-btn-ghost h-11 px-5" data-testid="email-cancel">Cancel</button>
            <button onClick={submit} className="kid-btn kid-btn-primary h-11 px-5" data-testid="email-generate"><Wand2 size={16} /> Generate draft</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
